import tokenManager from "@/tokenManager";

const AUTH_TERMINATION_CODE = "AUTH_SESSION_TERMINATED";
let terminationStarted = false;

const safeCommit = (store, mutation, payload) => {
  if (!store) return;

  try {
    store.commit(mutation, payload);
  } catch (_) {
    // Session cleanup must continue even if a store module is unavailable.
  }
};

const clearApplicationCaches = () => {
  if (typeof window === "undefined" || !("caches" in window)) return;

  window.caches
    .keys()
    .then((cacheNames) => Promise.all(cacheNames.map((name) => window.caches.delete(name))))
    .catch(() => { });
};

export const markAuthenticationTermination = (error) => {
  if (error && typeof error === "object") {
    error.code = AUTH_TERMINATION_CODE;
    error.isAuthenticationTermination = true;
  }

  return error;
};

export const isAuthenticationTermination = (error) =>
  error?.code === AUTH_TERMINATION_CODE || error?.isAuthenticationTermination === true;

let inactivityTimer = null;
let inactivityStatusInterval = null;
let appStore = null;
let trackingInitialized = false;

const DEFAULT_INACTIVITY_TIMEOUT_HOURS = 3;
const configuredInactivityTimeoutHours = Number.parseFloat(
  process.env.VUE_APP_INACTIVITY_TIMEOUT_HOURS
);

export const INACTIVITY_TIMEOUT_HOURS =
  Number.isFinite(configuredInactivityTimeoutHours) && configuredInactivityTimeoutHours > 0
    ? configuredInactivityTimeoutHours
    : DEFAULT_INACTIVITY_TIMEOUT_HOURS;
export const INACTIVITY_TIMEOUT_MS = INACTIVITY_TIMEOUT_HOURS * 60 * 60 * 1000;
export const LAST_API_ACTIVITY_STORAGE_KEY = "last_authenticated_api_activity_at";
const INACTIVITY_STATUS_UPDATE_INTERVAL_MS = 60 * 1000;

const hasActiveSession = (store) => {
  if (typeof window === "undefined") {
    return false;
  }

  const hasStoreLoginFlag = Boolean(
    store?.getters?.isLoggedIn
    || store?.state?.authorization?.isLoggedIn
  );
  let hasStoredUser = false;
  let hasStoredToken = false;

  try {
    hasStoredUser = Boolean(window.localStorage.getItem("user"));
    hasStoredToken = Boolean(window.localStorage.getItem("access_token"));
  } catch (_) {
    // The in-memory store remains usable if storage is unavailable.
  }

  return hasStoreLoginFlag || hasStoredUser || hasStoredToken;
};

const readLastApiActivity = () => {
  if (typeof window === "undefined") return null;

  try {
    const value = Number(window.localStorage.getItem(LAST_API_ACTIVITY_STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (_) {
    return null;
  }
};

const getInactivityDeadlineState = ({
  now = Date.now(),
  timeoutMs = INACTIVITY_TIMEOUT_MS,
} = {}) => {
  const lastActivityAt = readLastApiActivity();
  if (lastActivityAt === null || lastActivityAt > now) {
    return { deadlineAt: null, isActive: false, remainingMs: 0 };
  }

  const remainingMs = timeoutMs - (now - lastActivityAt);
  return {
    deadlineAt: lastActivityAt + timeoutMs,
    isActive: remainingMs > 0,
    remainingMs: Math.max(remainingMs, 0),
  };
};

const logInactivityStatus = ({
  now = Date.now(),
  timeoutMs = INACTIVITY_TIMEOUT_MS,
} = {}) => {
  if (typeof window === "undefined" || typeof window.console?.info !== "function") return;

  const deadline = getInactivityDeadlineState({ now, timeoutMs });
  if (!deadline.isActive) return;

  const totalMinutes = Math.ceil(deadline.remainingMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const expiresAt = new Date(deadline.deadlineAt).toLocaleString();

  window.console.info(
    `[Session] Logged in until ${expiresAt}. Time remaining: ${hours} hour(s) ${minutes} minute(s).`
  );
};

const startInactivityStatusUpdates = ({
  now = Date.now(),
  timeoutMs = INACTIVITY_TIMEOUT_MS,
} = {}) => {
  logInactivityStatus({ now, timeoutMs });
  inactivityStatusInterval = setInterval(() => {
    logInactivityStatus({ timeoutMs });
  }, INACTIVITY_STATUS_UPDATE_INTERVAL_MS);
};

export const setSessionStore = (storeInstance) => {
  appStore = storeInstance;
};

export const clearInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }

  if (inactivityStatusInterval) {
    clearInterval(inactivityStatusInterval);
    inactivityStatusInterval = null;
  }
};

export const expireInactiveSession = ({
  store,
  onExpire = null,
} = {}) => {
  const activeStore = store || appStore;
  clearInactivityTimer();

  if (terminationStarted) return false;

  if (typeof onExpire === "function") {
    onExpire();
    return true;
  }

  if (activeStore && typeof activeStore.dispatch === "function") {
    activeStore.dispatch("logout", { reason: "inactivity", immediate: true });
    return true;
  }

  return forceLoginRedirect({ store: activeStore });
};

export const enforceInactivityDeadline = ({
  store,
  now = Date.now(),
  timeoutMs = INACTIVITY_TIMEOUT_MS,
  onExpire = null,
} = {}) => {
  if (typeof window === "undefined") return true;

  const activeStore = store || appStore;
  if (!hasActiveSession(activeStore)) {
    clearInactivityTimer();
    return true;
  }

  const deadline = getInactivityDeadlineState({ now, timeoutMs });

  if (!deadline.isActive) {
    expireInactiveSession({ store: activeStore, onExpire });
    return false;
  }

  return true;
};

export const scheduleInactivityDeadline = ({
  store,
  now = Date.now(),
  timeoutMs = INACTIVITY_TIMEOUT_MS,
  onExpire = null,
} = {}) => {
  clearInactivityTimer();

  const activeStore = store || appStore;
  if (!hasActiveSession(activeStore)) return null;

  const deadline = getInactivityDeadlineState({ now, timeoutMs });
  if (!deadline.isActive) {
    expireInactiveSession({ store: activeStore, onExpire });
    return null;
  }

  inactivityTimer = setTimeout(() => {
    scheduleInactivityDeadline({ store: activeStore, timeoutMs, onExpire });
  }, deadline.remainingMs);
  startInactivityStatusUpdates({ now, timeoutMs });

  return inactivityTimer;
};

export const recordAuthenticatedApiActivity = ({
  store,
  now = Date.now(),
  timeoutMs = INACTIVITY_TIMEOUT_MS,
  onExpire = null,
} = {}) => {
  if (typeof window === "undefined") return null;

  const activeStore = store || appStore;
  if (!hasActiveSession(activeStore)) {
    clearInactivityTimer();
    return null;
  }

  try {
    window.localStorage.setItem(LAST_API_ACTIVITY_STORAGE_KEY, String(now));
  } catch (_) {
    expireInactiveSession({ store: activeStore, onExpire });
    return null;
  }

  return scheduleInactivityDeadline({ store: activeStore, now, timeoutMs, onExpire });
};

const handlePageShow = () => {
  scheduleInactivityDeadline({ store: appStore });
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") handlePageShow();
};

const handleStorageChange = (event) => {
  if (event.key !== LAST_API_ACTIVITY_STORAGE_KEY) return;

  if (event.newValue === null) {
    // The tab that cleared the shared activity key may also have removed the
    // user/token before this event is delivered. Treat removal itself as the
    // cross-tab logout signal instead of re-checking already-cleared storage.
    expireInactiveSession({ store: appStore });
    return;
  }

  handlePageShow();
};

export const initializeInactivityTracking = ({
  store,
} = {}) => {
  if (typeof window === "undefined") return () => {};

  setSessionStore(store || appStore);

  if (!trackingInitialized) {
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    trackingInitialized = true;
  }

  handlePageShow();

  return disposeInactivityTracking;
};

export const disposeInactivityTracking = () => {
  clearInactivityTimer();
  if (typeof window === "undefined" || !trackingInitialized) return;

  window.removeEventListener("pageshow", handlePageShow);
  window.removeEventListener("storage", handleStorageChange);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  trackingInitialized = false;
};

export const resetTerminationState = () => {
  terminationStarted = false;
};

export const clearClientSession = ({ store, preserveKeys = ["darkMode", "language"] } = {}) => {
  const preservedValues = {};

  try {
    preserveKeys.forEach((key) => {
      const value = window.localStorage.getItem(key);
      if (value !== null) preservedValues[key] = value;
    });
  } catch (_) {
    // Storage can be unavailable in restricted/private browser contexts.
  }

  try {
    tokenManager.clearToken();
  } catch (_) {
    // Continue clearing the remaining session data.
  }

  try {
    const keysToRemove = [];
    for (const key of Object.keys(window.localStorage)) {
      if (!preserveKeys.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key));

    Object.entries(preservedValues).forEach(([key, value]) => {
      window.localStorage.setItem(key, value);
    });
  } catch (_) {
    // Continue with session and in-memory cleanup.
  }

  try {
    window.sessionStorage.clear();
  } catch (_) {
    // Continue with in-memory cleanup.
  }

  safeCommit(store, "setAccessToken", "");
  safeCommit(store, "setLoggedUser", {});
  safeCommit(store, "setIsLoggedIn", false);
  safeCommit(store, "clearAuthorization");
  safeCommit(store, "setRolePermissions", []);
  safeCommit(store, "setPermissions", []);
  safeCommit(store, "setRoles", []);
  safeCommit(store, "setRole", {});
  safeCommit(store, "setSelectedRole", {});
  safeCommit(store, "setToast", {
    display: false,
    type: null,
    title: null,
    message: null,
  });

  clearApplicationCaches();
};

export const forceLoginRedirect = ({ store } = {}) => {
  if (terminationStarted) return false;

  terminationStarted = true;
  clearInactivityTimer();
  clearClientSession({ store, preserveKeys: ["darkMode", "language"] });

  if (typeof window !== "undefined") {
    const loginUrl = `${process.env.BASE_URL || "/"}login`.replace(/\/+/g, "/");
    window.location.replace(loginUrl);
  }

  return true;
};

export const hasSessionTerminationStarted = () => terminationStarted;
